/**
 * ArcherLoader + ArcherSpinner
 *
 * Two-tier loading system:
 *   ArcherLoader  — full-size animated logo (fullscreen splash, page-level loading).
 *                   The SVG handles all motion (ring spin, dot pulse, data packets).
 *                   Zero outer animation — no scaling, no bouncing, no glow wrapper.
 *   ArcherSpinner — tiny comet-arc spinner for inline states (forms, buttons, rows).
 *                   Mirrors the archer ring aesthetic at 16–32px.
 */

import React from 'react'
import { Box, Typography, useTheme } from '@mui/material'
import { keyframes } from '@mui/material/styles'
import { spacing, typography } from '../../tokens'

// ─── ArcherSpinner ─────────────────────────────────────────────────────────────

const arcSpin = keyframes`
  from { transform: rotate(0deg); }
  to   { transform: rotate(360deg); }
`

export interface ArcherSpinnerProps {
  /** Diameter in pixels. Default: 20 */
  size?: number
  /** Stroke color. Default: brand accent #E8734A */
  color?: string
}

export const ArcherSpinner: React.FC<ArcherSpinnerProps> = ({
  size = 20,
  color = '#E8734A',
}) => {
  const sw = size <= 20 ? 2 : size <= 32 ? 2.5 : 3
  const r = (size - sw * 2) / 2
  const cx = size / 2
  const cy = size / 2
  const circ = 2 * Math.PI * r
  const arc  = circ / 4   // 1/4 circumference — matches the logo comet tail

  return (
    <Box
      component="svg"
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      sx={{
        display: 'inline-block',
        flexShrink: 0,
        verticalAlign: 'middle',
        animation: `${arcSpin} 1.2s linear infinite`,
      }}
    >
      {/* Dim ambient track */}
      <circle
        cx={cx} cy={cy} r={r}
        fill="none"
        stroke={color}
        strokeOpacity={0.18}
        strokeWidth={sw}
      />
      {/* Comet-tail arc */}
      <circle
        cx={cx} cy={cy} r={r}
        fill="none"
        stroke={color}
        strokeWidth={sw}
        strokeLinecap="round"
        strokeDasharray={`${arc} ${circ - arc}`}
      />
    </Box>
  )
}

// ─── ArcherLoader ──────────────────────────────────────────────────────────────

export interface ArcherLoaderProps {
  /**
   * Width of the logo in pixels. Height scales automatically (340:420 ratio).
   * Recommended: 260–300 for fullscreen, 80–120 for page sections.
   */
  size?: number
  /** Text shown below the logo */
  message?: string
  /** Fixed fullscreen overlay — covers the entire viewport */
  fullscreen?: boolean
}

export const ArcherLoader: React.FC<ArcherLoaderProps> = ({
  size = 260,
  message,
  fullscreen = false,
}) => {
  const theme = useTheme()

  const content = (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: spacing.scale[3],
      }}
    >
      {/*
        No wrapper animation — archer_logo.svg is self-animating.
        Ring spins, dot pulses, data packets fly. The SVG handles everything.
        Scaling it with CSS would make it jump at the start of each loop.
      */}
      <img
        src="/archer_logo.svg"
        alt="Archer"
        draggable={false}
        style={{ width: size, height: 'auto' }}
      />

      {message && (
        <Typography
          variant="body2"
          sx={{
            color: theme.palette.text.secondary,
            fontWeight: typography.fontWeight.medium,
            fontSize: typography.fontSize.sm,
            letterSpacing: '0.06em',
            textAlign: 'center',
          }}
        >
          {message}
        </Typography>
      )}
    </Box>
  )

  if (fullscreen) {
    return (
      <Box
        sx={{
          position: 'fixed',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: theme.palette.background.default,
          zIndex: 9999,
        }}
      >
        {content}
      </Box>
    )
  }

  return content
}

export default ArcherLoader
